import React, { useCallback, useEffect, useState } from "react";
import { NextPage } from "next";
import {
    BlogVisitorPopup,
    HTMLHeader,
    LoadingIndicator,
    SharePopup,
} from "@Modules/common";
import { IHTMLHeader } from "@Modules/common/HTMLHeader/HTMLHeader";
import {
    createAction,
    API_ACTION_TYPES,
    ACTION_TYPES,
    SELECTORS,
    isBrowser,
    useCanRender,
    useScrollToSectionOnMount,
    ROUTES,
    REACTION_TYPES,
    getVisitorDetails,
    MODAL_TYPES,
    useModal,
    useFetchStatus,
    FETCH_STATUS,
} from "@Utils";
import { DEFAULT_PROFILE_PIC_SRC } from "repoV2/utils/common/assets/assets";
import { useDispatch, useSelector } from "react-redux";
import { IBlogPostPage } from "@Interfaces";
import {
    BlogPostBody,
    BlogPostFooter,
    BlogPostHeader,
} from "@Modules/blogPost";
import { LOADING_INDICATOR_DESIGNS } from "repoV2/components/common/LoadingIndicators/LoadingIndicator.data";
import styles from "./blogPost.module.scss";

const BlogPost: NextPage<IBlogPostPage.IProps, IBlogPostPage.InitialProps> = ({
    hostName,
    blogPostUuid,
    preview,
}: IBlogPostPage.IProps) => {
    const host = useSelector(SELECTORS.host);
    const hostData = host?.dataHost?.profile_data;

    const { blogPost } = host;

    const dispatch = useDispatch();

    const canRender = useCanRender(!!blogPost?.uuid);

    const fetchBlogPostsStatus = useFetchStatus(
        API_ACTION_TYPES.FETCH_BLOG_POST_DETAILS
    );

    const postAnalyticalEvent = (): void => {
        dispatch(
            createAction(ACTION_TYPES.UTILS.API_CALL, {
                apiActionType: API_ACTION_TYPES.BLOG_POST_ANALYTICS,
                urlArgs: { hostName },
                payload: {
                    blog_post_uuid: blogPostUuid,
                    event_id: 1,
                },
            })
        );
    };

    useEffect(() => {
        if (fetchBlogPostsStatus === FETCH_STATUS.FETCH_ERROR) {
            if (isBrowser()) {
                window.location.href = `https://${hostName}.${process.env.NEXT_PUBLIC_PROJECT_DOMAIN}/blog`;
            }
        } else if (!preview) {
            postAnalyticalEvent();
        }
    }, []);

    const { openModal, closeModal } = useModal();
    const handlePreviewPopup = useCallback((): void => {
        if (preview) {
            openModal(MODAL_TYPES.PreviewPopup, {
                modalProps: {
                    onClickBack: closeModal,
                    onClickContinue: closeModal,
                },
            });
        }
    }, []);

    const htmlHeaderProps: IHTMLHeader.IProps = {
        title: `${blogPost?.title} | ${
            hostData?.name ? `${hostData.name}'s ` : ""
        }Blog`,
        favicon: hostData?.profile_pic || DEFAULT_PROFILE_PIC_SRC,
    };

    useScrollToSectionOnMount(1000);

    const [showBlogVisitorPopup, setShowBlogVisitorPopup] = useState(false);
    const openBlogVisitorPopup = () => {
        if (preview) {
            handlePreviewPopup();
            return;
        }
        setShowBlogVisitorPopup(true);
    };
    const closeBlogVisitorPopup = () => {
        setShowBlogVisitorPopup(false);
    };

    const [showSharePopup, setShowSharePopup] = useState(false);
    const openSharePopup = () => {
        if (preview) {
            handlePreviewPopup();
            return;
        }
        setShowSharePopup(true);
    };
    const closeSharePopup = () => {
        setShowSharePopup(false);
    };

    const postRoute = `${ROUTES.BLOG_POST.urlPath}/${blogPost?.uuid}`;
    let shareUrl = "";
    if (isBrowser()) {
        shareUrl = `${window.location.origin}${postRoute}`;
    }

    const [userReactionType, setUserReactionType] = useState(
        REACTION_TYPES.NONE
    );
    const [userReactionDelta, setUserReactionDelta] = useState(0);
    const [userReactionDeltaType, setUserReactionDeltaType] = useState(
        REACTION_TYPES.NONE
    );

    useEffect(() => {
        // The blog data is being fetched again to fetch the reaction of the visitor,
        // which cannot be fetched on the initial SSR call in getInitialProps since
        // the customer's cached auth token can't be determined.
        dispatch(
            createAction(ACTION_TYPES.UTILS.API_CALL, {
                apiActionType: API_ACTION_TYPES.FETCH_BLOG_POST_DETAILS,
                urlArgs: { hostName, blogPostUuid, preview: !!preview },
            })
        );
    }, []);

    useEffect(() => {
        setUserReactionType(blogPost?.visitor_reaction || REACTION_TYPES.NONE);
    }, [blogPost?.visitor_reaction]);

    const reactionClick = (reactionType: number) => {
        if (preview) {
            handlePreviewPopup();
            return;
        }
        const visitorDetails = getVisitorDetails();
        if (!visitorDetails.uuid) {
            openBlogVisitorPopup();
        } else {
            const undo = reactionType === userReactionType;

            dispatch(
                createAction(ACTION_TYPES.UTILS.API_CALL, {
                    apiActionType: API_ACTION_TYPES.UPDATE_BLOG_REACTION,
                    urlArgs: {
                        blogPostUuid: blogPost?.uuid,
                    },
                    payload: {
                        visitor_uuid: visitorDetails.uuid,
                        reaction: reactionType,
                        reaction_undo: undo,
                    },
                    successCallback: () => {
                        setUserReactionType(
                            undo ? REACTION_TYPES.NONE : reactionType
                        );
                        setUserReactionDelta(
                            userReactionDelta + (undo ? -1 : 1)
                        );
                        setUserReactionDeltaType(reactionType);
                    },
                })
            );
        }
    };

    const likeCount =
        (blogPost?.like_count || 0) +
        (userReactionDeltaType === REACTION_TYPES.LIKE ? userReactionDelta : 0);

    const onLikeClick = () => {
        reactionClick(REACTION_TYPES.LIKE);
    };

    const commentClick = () => {
        if (preview) {
            handlePreviewPopup();
            return;
        }
        if (isBrowser()) {
            window.location.hash = "";
            window.location.hash = "comments";
        }
    };

    const [commentCountDelta, setCommentCountDelta] = useState(0);
    const onCreateComment = () => {
        setCommentCountDelta(commentCountDelta + 1);
    };
    const commentCount = (blogPost?.comment_count || 0) + commentCountDelta;

    if (!canRender && !blogPost) {
        return (
            <>
                <HTMLHeader {...htmlHeaderProps} />
                <div className={styles.root}>
                    <div className={styles.loaderContainer}>
                        <LoadingIndicator
                            applyTheme
                            design={LOADING_INDICATOR_DESIGNS.rotating_arcs}
                        />
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <HTMLHeader {...htmlHeaderProps} />
            <div className={styles.root} id="root">
                <BlogPostHeader
                    title={blogPost?.title}
                    preview={preview}
                    handlePreviewPopup={handlePreviewPopup}
                />
                <BlogPostBody
                    data={blogPost}
                    hostName={hostName}
                    openBlogVisitorPopup={openBlogVisitorPopup}
                    openSharePopup={openSharePopup}
                    userReactionType={userReactionType}
                    likeCount={likeCount}
                    onLikeClick={onLikeClick}
                    commentCount={commentCount}
                    commentClick={commentClick}
                    onCreateComment={onCreateComment}
                    preview={preview}
                    handlePreviewPopup={handlePreviewPopup}
                />
                <BlogPostFooter
                    openSharePopup={openSharePopup}
                    userReactionType={userReactionType}
                    likeCount={likeCount}
                    onLikeClick={onLikeClick}
                    commentCount={commentCount}
                    commentClick={commentClick}
                />
                {showSharePopup ? (
                    <SharePopup
                        closePopup={closeSharePopup}
                        popupTitle="Share this Blog Post"
                        shareTitle={blogPost?.title || ""}
                        shareUrl={shareUrl}
                        shareText={`${blogPost?.title || ""} - ${shareUrl}`}
                    />
                ) : null}
                {showBlogVisitorPopup ? (
                    <BlogVisitorPopup closePopup={closeBlogVisitorPopup} />
                ) : null}
            </div>
        </>
    );
};

BlogPost.getInitialProps = async (
    ctx: IBlogPostPage.IPageContext
): Promise<IBlogPostPage.InitialProps> => {
    const { hostName } = ctx.params;

    const { blogPostUuid, preview }: IBlogPostPage.IRouterQuery = ctx.query;

    ctx.store.dispatch(
        createAction(ACTION_TYPES.UTILS.API_CALL, {
            apiActionType: API_ACTION_TYPES.FETCH_HOST,
            urlArgs: { hostName },
        })
    );
    ctx.store.dispatch(
        createAction(ACTION_TYPES.UTILS.API_CALL, {
            apiActionType: API_ACTION_TYPES.FETCH_HOST_THEME,
            urlArgs: { hostName },
        })
    );

    ctx.store.dispatch(
        createAction(ACTION_TYPES.UTILS.API_CALL, {
            apiActionType: API_ACTION_TYPES.FETCH_BLOG_POST_DETAILS,
            urlArgs: { hostName, blogPostUuid, preview: !!preview },
        })
    );

    return {
        hostName,
        blogPostUuid: blogPostUuid!,
        preview: !!preview,
    };
};

export default BlogPost;
